import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { IFile } from '@repo/toolkit/types'
import { UserTwoIcon } from '@repo/ui/icons'
import MultiSelectInput from '@repo/ui/multiselect-input'
import { useQuery } from '@tanstack/react-query'
import { useFormContext } from 'react-hook-form'

import { getHobbies } from '../../../api/hobbies/get-hobbies'
import useTranslate from '../../../lib/hooks/use-translate'
import { HobbyAudienceEnum } from '../../../lib/types/enums/hobby/hobby-audience.enum'
import {
  MaritalStatusEnum,
  maritalStatusEnumMapping
} from '../../../lib/types/enums/staff-application/marital-status.enum'
import { StaffInput } from '../../../lib/types/models/staff/staff-input'
import { getSelectedEnumOption } from '../../../lib/utils/functions/misc.functions'
import { FormControl, FormField, FormItem } from '../../elements/forms/form'
import IconBox from '../../elements/icon-box'
import { ControlledAdressInput } from '../../elements/inputs/address-input'
import ControlledInput from '../../elements/inputs/controlled-input'
import ControlledPhoneInput from '../../elements/inputs/controlled-phone-input'
import ControlledSelectInput from '../../elements/inputs/controlled-select-input'
import StaffPersonalSpacePictureUpload from './staff-personal-space-picture-upload'

type Props = {
  disabled?: boolean
  picture?: Pick<IFile, 'path'> | null
  onUpdateProfilePicture: (file: IFile) => void
}

const StaffPersonalSpaceInformationForm = ({
  disabled,
  picture,
  onUpdateProfilePicture
}: Props) => {
  const { t } = useTranslate()

  const form = useFormContext<StaffInput>()

  const { data: hobbies } = useQuery({
    queryKey: ['hobbies', { automaticallyGenerated: false, visibleTo: [HobbyAudienceEnum.STAFF] }],
    queryFn: () =>
      getHobbies({
        filter: { automaticallyGenerated: false, visibleTo: [HobbyAudienceEnum.STAFF] }
      })
  })

  return (
    <IconBox
      title={t('staff.personal.space.personal.info')}
      icon={UserTwoIcon}
      className="flex flex-col gap-3"
      titleColor={tailwindColors.black.DEFAULT}
      titleVariant="h6"
    >
      <StaffPersonalSpacePictureUpload
        picture={picture}
        onUpdateProfilePicture={onUpdateProfilePicture}
        disabled={disabled}
      />
      <div className="w-full flex items-center gap-3 mobile:flex-col">
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormControl>
                <ControlledInput {...field} label="Email" type="email" />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="phoneNumber"
          render={({ field }) => (
            <FormItem className={cn('w-full', { 'pointer-events-none': disabled })}>
              <FormControl>
                <ControlledPhoneInput
                  {...field}
                  label="Téléphone"
                  disabled={disabled}
                  placeholder={'Téléphone'}
                  defaultCountry="FR"
                />
              </FormControl>
            </FormItem>
          )}
        />
      </div>
      <div className="w-full flex items-center gap-3  mobile:flex-col">
        <FormField
          control={form.control}
          name="address1"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormControl>
                <ControlledAdressInput
                  {...field}
                  label={t('staff.personal.space.address')}
                  onDetailsChange={(placeDetails) => {
                    form.setValue('address1', placeDetails.address, { shouldValidate: true })
                    if (placeDetails.postalCode || !form.getValues().postalCode) {
                      form.setValue('postalCode', placeDetails.postalCode, {
                        shouldValidate: true
                      })
                    }
                    if (placeDetails.city || !form.getValues().city) {
                      form.setValue('city', placeDetails.city, { shouldValidate: true })
                    }
                    if (placeDetails.country || !form.getValues().country) {
                      form.setValue('country', placeDetails.country, { shouldValidate: true })
                    }
                  }}
                />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="address2"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormControl>
                <ControlledInput
                  {...field}
                  label={t('staff.personal.space.supplementary.address')}
                  type="text"
                />
              </FormControl>
            </FormItem>
          )}
        />
      </div>

      <div className="flex gap-2  mobile:flex-col">
        <FormField
          control={form.control}
          name="postalCode"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormControl>
                <ControlledInput
                  {...field}
                  label={t('staff.personal.space.postal.code')}
                  type="text"
                />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="city"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormControl>
                <ControlledInput {...field} label={t('staff.personal.space.city')} type="text" />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="country"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormControl>
                <ControlledInput
                  {...field}
                  label={t('staff.personal.space.country')}
                  type="text"
                />
              </FormControl>
            </FormItem>
          )}
        />
      </div>
      <div className="flex gap-2  mobile:flex-col">
        <FormField
          control={form.control}
          name="maritalStatus"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormControl>
                <ControlledSelectInput
                  {...field}
                  label={t('staff.personal.space.civil.status')}
                  options={Object.values(MaritalStatusEnum).map((status) => ({
                    label: t(maritalStatusEnumMapping[status]),
                    value: status
                  }))}
                  selectedOption={getSelectedEnumOption(field.value, maritalStatusEnumMapping, t)}
                />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="numberOfChildren"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormControl>
                <ControlledInput
                  {...field}
                  type="number"
                  isControllable
                  allowNegative={false}
                  label={t('staff.personal.space.number.of.children')}
                />
              </FormControl>
            </FormItem>
          )}
        />
      </div>
      <FormField
        control={form.control}
        name="hobbyIds"
        render={({ field }) => (
          <FormItem className="w-full">
            <FormControl>
              <MultiSelectInput
                {...field}
                label={t('staff.personal.space.hobbies')}
                variant="primary"
                searchable
                options={hobbies?.data?.map((hobby) => ({
                  label: hobby.name,
                  value: hobby.id
                }))}
                selectedOptions={field.value?.map((id) => ({
                  label: hobbies?.data?.find((hobby) => hobby.id === id)?.name ?? '',
                  value: id
                }))}
              />
            </FormControl>
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="allergies"
        render={({ field }) => (
          <FormItem className="w-full">
            <FormControl>
              <ControlledInput
                {...field}
                label={t('staff.personal.space.allergies')}
                type="text"
              />
            </FormControl>
          </FormItem>
        )}
      />
    </IconBox>
  )
}

export default StaffPersonalSpaceInformationForm
